/**
 * 顧客管理モーダルコンポーネント
 *
 * 追加・編集・削除確認のモーダル群
 */

import {
  Stack,
  Paper,
  Group,
  Text,
  TextInput,
  Textarea,
  Modal,
  Button,
  Badge,
  Code,
  CopyButton,
  Switch,
  Tooltip,
  Alert,
} from "@mantine/core";
import { Mail, Copy, Check, Trash2, Settings } from "lucide-react";
import { Customer, generateFaxAddress } from "../../services/customerService";

// Timestamp を日本語日時に変換
function formatTimestamp(ts: { _seconds: number } | undefined): string {
  if (!ts) return "-";
  const d = new Date(ts._seconds * 1000);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${year}年${month}月${day}日 ${hours}時${minutes}分${seconds}秒`;
}

// =============== 追加モーダル ===============

interface AddCustomerModalProps {
  opened: boolean;
  onClose: () => void;
  customerId: string;
  setCustomerId: (value: string) => void;
  customerName: string;
  setCustomerName: (value: string) => void;
  notes: string;
  setNotes: (value: string) => void;
  onAdd: () => void;
  adding: boolean;
}

export function AddCustomerModal({
  opened,
  onClose,
  customerId,
  setCustomerId,
  customerName,
  setCustomerName,
  notes,
  setNotes,
  onAdd,
  adding,
}: AddCustomerModalProps) {
  return (
    <Modal opened={opened} onClose={onClose} title="顧客を追加" size="lg">
      <Stack gap="md">
        {/* 顧客ID - 重要な設定として強調 */}
        <Paper
          p="md"
          withBorder
          radius="md"
          style={{
            borderColor: "var(--mantine-color-orange-4)",
            backgroundColor: "var(--mantine-color-orange-light)",
          }}
        >
          <Group gap="xs" mb="sm">
            <Badge
              color="orange"
              variant="filled"
              size="sm"
              leftSection={<Settings size={12} />}
            >
              重要な設定
            </Badge>
            <Text size="sm" fw={600}>
              顧客ID（サブアドレス）
            </Text>
          </Group>

          <TextInput
            placeholder="例: customer001"
            required
            value={customerId}
            onChange={(e) => setCustomerId(e.currentTarget.value)}
            styles={{
              input: {
                fontFamily: "monospace",
                fontSize: "14px",
                fontWeight: 500,
              },
            }}
          />

          <Stack gap={4} mt="sm">
            <Text size="xs" c="dimmed">
              • 半角英数字、ハイフン（-）、アンダースコア（_）のみ使用可能
            </Text>
            <Text size="xs" c="dimmed">
              • FAX転送先メールアドレスの「+」以降に使用されます
            </Text>
            <Group gap={4}>
              <Text size="xs" c="red" fw={500}>
                ⚠ この値は登録後に変更できません
              </Text>
            </Group>
          </Stack>
        </Paper>

        {/* FAXアドレスプレビュー - 常に表示 */}
        <Paper p="md" withBorder radius="md" bg="var(--mantine-color-gray-light)">
          <Group gap="xs" mb="xs">
            <Mail size={16} />
            <Text size="sm" fw={500}>
              FAX転送先アドレス（プレビュー）
            </Text>
          </Group>

          {customerId ? (
            <>
              <Code block style={{ fontSize: "13px" }}>
                {generateFaxAddress(customerId)}
              </Code>
              <Text size="xs" c="dimmed" mt="xs">
                顧客のFAX機にこのアドレスを転送先として設定してください。
                このアドレス宛に送信されたFAXがOCR処理されます。
              </Text>
            </>
          ) : (
            <Text size="sm" c="dimmed" fs="italic">
              顧客IDを入力するとアドレスが表示されます
            </Text>
          )}
        </Paper>

        <TextInput
          label="顧客名"
          description="帳票上の表記ゆれはパターン登録で対応できます"
          placeholder="例: テスト株式会社"
          required
          value={customerName}
          onChange={(e) => setCustomerName(e.currentTarget.value)}
        />

        <Textarea
          label="備考"
          placeholder="メモ（任意）"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            キャンセル
          </Button>
          <Button onClick={onAdd} loading={adding}>
            追加
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// =============== 編集モーダル ===============

interface EditCustomerModalProps {
  customer: Customer | null;
  onClose: () => void;
  editName: string;
  setEditName: (value: string) => void;
  editNotes: string;
  setEditNotes: (value: string) => void;
  editStatus: "active" | "inactive";
  setEditStatus: (value: "active" | "inactive") => void;
  onUpdate: () => void;
  saving: boolean;
  onDelete: () => void;
}

export function EditCustomerModal({
  customer,
  onClose,
  editName,
  setEditName,
  editNotes,
  setEditNotes,
  editStatus,
  setEditStatus,
  onUpdate,
  saving,
  onDelete,
}: EditCustomerModalProps) {
  return (
    <Modal
      opened={!!customer}
      onClose={onClose}
      title="顧客詳細・編集"
      size="md"
    >
      {customer && (
        <Stack gap="lg">
          {/* FAX転送先アドレス */}
          <Paper p="md" withBorder radius={8}>
            <Group gap="xs" mb="xs">
              <Mail size={16} />
              <Text size="sm" fw={500}>
                FAX転送先アドレス
              </Text>
            </Group>
            <Group gap="xs">
              <Code>{generateFaxAddress(customer.customerId)}</Code>
              <CopyButton value={generateFaxAddress(customer.customerId)}>
                {({ copied, copy }) => (
                  <Button
                    size="xs"
                    variant="light"
                    onClick={copy}
                    leftSection={copied ? <Check size={14} /> : <Copy size={14} />}
                  >
                    {copied ? "コピーしました" : "コピー"}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Paper>

          {/* ステータス */}
          <Stack gap="xs">
            <Group gap="sm">
              <Switch
                checked={editStatus === "active"}
                onChange={() =>
                  setEditStatus(editStatus === "active" ? "inactive" : "active")
                }
                color="green"
                styles={{
                  track: { cursor: "pointer" },
                }}
              />
              <Text size="sm" fw={500}>
                {editStatus === "active" ? "有効" : "無効"}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {editStatus === "active"
                ? "この顧客からのFAXは処理されます。"
                : "この顧客からのFAXは処理されません。"}
            </Text>
          </Stack>

          {/* 編集可能項目 */}
          <TextInput
            label="顧客名"
            required
            value={editName}
            onChange={(e) => setEditName(e.currentTarget.value)}
          />
          <Textarea
            label="備考"
            value={editNotes}
            onChange={(e) => setEditNotes(e.currentTarget.value)}
          />

          {/* 登録日・更新日 */}
          <Group gap="xl">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                登録日
              </Text>
              <Text size="sm">{formatTimestamp(customer.createdAt)}</Text>
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                更新日
              </Text>
              <Text size="sm">{formatTimestamp(customer.updatedAt)}</Text>
            </Stack>
          </Group>

          {/* アクションボタン */}
          <Group justify="flex-end" gap="sm">
            <Tooltip
              label="削除するには先に無効化してください"
              disabled={editStatus === "inactive"}
            >
              <Button
                color="red"
                variant="outline"
                onClick={onDelete}
                style={{ marginRight: "auto" }}
                disabled={editStatus === "active"}
                leftSection={<Trash2 size={16} />}
              >
                削除
              </Button>
            </Tooltip>

            <Button variant="default" onClick={onClose}>
              キャンセル
            </Button>
            <Button onClick={onUpdate} loading={saving}>
              保存
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

// =============== 削除確認モーダル ===============

interface DeleteConfirmModalProps {
  customer: Customer | null;
  onClose: () => void;
  onDelete: () => void;
  deleting: boolean;
}

export function DeleteConfirmModal({
  customer,
  onClose,
  onDelete,
  deleting,
}: DeleteConfirmModalProps) {
  return (
    <Modal opened={!!customer} onClose={onClose} title="顧客を削除">
      <Stack gap="md">
        <Text>
          <strong>{customer?.customerName}</strong> を削除しますか？
        </Text>
        <Alert color="red" title="警告">
          この操作は取り消せません。関連するデータも削除されます。
        </Alert>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            color="red"
            onClick={onDelete}
            loading={deleting}
            leftSection={<Trash2 size={16} />}
          >
            削除
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
